import { MappedLeadData } from '../types';

export type ResearchSummaryTab = 'person' | 'company' | 'signals' | 'outreach';

export interface ResearchSummarySection {
  title: string;
  normalizedTitle: string;
  content: string;
}

const TITLE_MATCHERS: Record<ResearchSummaryTab, RegExp[]> = {
  person: [
    /psychographic profile/,
    /professional context/,
    /hobbies.*interests?/,
    /background.*credentials/,
  ],
  company: [
    /company context/,
  ],
  signals: [
    /strategic intelligence/,
    /digital footprint.*activity/,
    /recent intelligence/,
  ],
  outreach: [
    /outreach (?:strategy|optimization)/,
  ],
};

const INVALID_SUMMARY_PATTERNS = [
  /^sorry,\s*no high score results were found\.?$/i,
  /^no data provided/i,
];

const TITLE_KEYCAP_PATTERN = /[0-9#*]\uFE0F?\u20E3/gu;
const TITLE_EMOJI_PATTERN = /[\p{Extended_Pictographic}\p{Emoji_Presentation}\p{Emoji_Modifier}\uFE0F\u200D\u20E3]/gu;
const CONTENT_LEADING_DECORATION_PATTERN = /^([ \t]*(?:(?:[-+*]|\d+\.)[ \t]+)?(?:#{1,6}[ \t]+)?(?:(?:\*\*|__)[ \t]*)?)(?![©®™](?!\uFE0F))(?:(?:[0-9#*]\uFE0F?\u20E3|[\p{Extended_Pictographic}\p{Emoji_Modifier}\uFE0F\u200D]+)[ \t]*)+/u;
const MARKDOWN_THEMATIC_BREAK_PATTERN = /^(?:(?:-[ \t]*){3,}|(?:_[ \t]*){3,}|(?:\*[ \t]*){3,})$/;
const VALIDATION_COMPLETE_PATTERN = /^validation complete:?$/i;

export const cleanResearchSummaryTitle = (title: string) =>
  title
    .replace(/^##\s*/, '')
    .replace(/\*\*/g, '')
    .replace(/__/g, '')
    .replace(TITLE_KEYCAP_PATTERN, ' ')
    .replace(TITLE_EMOJI_PATTERN, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[^\p{L}\p{N}]+/u, '')
    .trim();

export const isStandaloneResearchSummaryTitle = (line: string) => {
  const trimmedLine = line.trim();
  return /^(?:\*\*.+\*\*|__.+__|#{1,6}[ \t]+\S.*)$/.test(trimmedLine);
};

export const cleanResearchSummaryContent = (content: string) => {
  const cleanedLines: string[] = [];

  for (const line of content.split('\n')) {
    const lineWithoutDecoration = line.replace(CONTENT_LEADING_DECORATION_PATTERN, '$1');
    const contentWithoutMarkdown = lineWithoutDecoration
      .trim()
      .replace(/^(?:[-+*]|\d+\.)[ \t]+/, '')
      .replace(/^#{1,6}[ \t]+/, '')
      .replace(/\*\*|__/g, '')
      .trim();

    if (VALIDATION_COMPLETE_PATTERN.test(contentWithoutMarkdown)) break;
    cleanedLines.push(contentWithoutMarkdown ? lineWithoutDecoration : '');
  }

  while (cleanedLines.length > 0) {
    const finalLine = cleanedLines[cleanedLines.length - 1].trim();
    if (finalLine && !MARKDOWN_THEMATIC_BREAK_PATTERN.test(finalLine)) break;
    cleanedLines.pop();
  }

  return cleanedLines
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const normalizeSectionTitle = (title: string) =>
  cleanResearchSummaryTitle(title)
    .trim()
    .toLowerCase();

export const extractResearchSummary = (data: MappedLeadData): string | null => {
  const candidates = [
    data.researchSummary,
    data.personLvl2?.research_summary,
    data.personLvl1?.research_summary,
    data.rawOriginalData?.research_summary,
    data.rawOriginalData?.lvl2?.research_summary,
    data.rawOriginalData?.lvl1?.research_summary,
  ];

  for (const candidate of candidates) {
    if (typeof candidate !== 'string') continue;
    const trimmed = candidate.trim();
    if (!trimmed) continue;
    if (INVALID_SUMMARY_PATTERNS.some((pattern) => pattern.test(trimmed))) continue;
    return trimmed;
  }

  return null;
};

export const parseResearchSummarySections = (summary: string | null | undefined): ResearchSummarySection[] => {
  if (!summary) return [];

  const normalizedSummary = summary.replace(/\r\n/g, '\n').trim();
  if (!/^##[ \t]+/m.test(normalizedSummary)) return [];

  const sectionChunks = normalizedSummary
    .split(/\n(?=##[ \t]+)/g)
    .filter((chunk) => /^##[ \t]+/.test(chunk.trim()));

  return sectionChunks
    .map((chunk) => {
      const [header = '', ...bodyLines] = chunk.trim().split('\n');
      let title = cleanResearchSummaryTitle(header);
      let contentLines = bodyLines;
      const firstContentLineIndex = contentLines.findIndex((line) => line.trim().length > 0);
      const firstContentLine = firstContentLineIndex >= 0 ? contentLines[firstContentLineIndex].trim() : '';
      const firstContentLineLooksLikeTitle = isStandaloneResearchSummaryTitle(firstContentLine);

      if (!title && firstContentLineLooksLikeTitle) {
        title = cleanResearchSummaryTitle(firstContentLine);
        contentLines = contentLines.slice(firstContentLineIndex + 1);
      }

      const normalizedTitle = normalizeSectionTitle(title);
      const content = cleanResearchSummaryContent(contentLines.join('\n'));

      return { title, normalizedTitle, content };
    })
    .filter((section) => {
      if (!section.title || !section.content) return false;
      if (section.normalizedTitle === 'validation complete') return false;
      return true;
    });
};

export const getResearchSummarySectionsForTab = (
  data: MappedLeadData,
  tab: ResearchSummaryTab
): ResearchSummarySection[] => {
  const summary = extractResearchSummary(data);
  const sections = parseResearchSummarySections(summary);

  return sections.filter((section) =>
    TITLE_MATCHERS[tab].some((matcher) => matcher.test(section.normalizedTitle))
  );
};
